import { memo, type ReactElement } from 'react';
import { Group, Text, Input } from '@mantine/core';
import { IconUser, IconClock, IconLink } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { UserAvatar } from '@/components/user/UserAvatar';
import { useLocalizedDate } from '@/hooks/useLocalizedDate';
import { ICON_SIZE_TAB } from '@/constants/ui';
import type { Note } from '@/types/notes';
import type { RelatedTaskSummary } from '@/types';

import styles from './NoteMetadataSection.module.css';

interface NoteMetadataSectionProps {
  note: Note;
  linkedTask?: RelatedTaskSummary;
  onTaskNavigate?: (taskId: string, title: string) => void;
}

export const NoteMetadataSection = memo(function NoteMetadataSection({
  note,
  linkedTask,
  onTaskNavigate,
}: NoteMetadataSectionProps): ReactElement {
  const { t } = useTranslation();
  const { formatDateCompact } = useLocalizedDate();

  const displayDate = note.updatedAt ?? note.createdAt;

  const handleTaskClick = (): void => {
    if (linkedTask) {
      onTaskNavigate?.(linkedTask.id, linkedTask.title);
    }
  };

  return (
    <div className={styles.metadataGrid}>
      {/* Empty creatorName = identity hidden by a public share's personal-data flag. */}
      {note.creatorName && (
        <div className={styles.metadataItem}>
          <Group gap="xs" align="center">
            <IconUser size={ICON_SIZE_TAB} />
            <Input.Label>{t('notes.detail.author')}</Input.Label>
          </Group>
          <Group gap="xs" align="center">
            <UserAvatar
              displayName={note.creatorName}
              avatarUrl={note.creatorAvatarUrl}
              size="sm"
            />
            <Text size="sm" className={styles.metadataValue}>
              {note.creatorName}
            </Text>
          </Group>
        </div>
      )}

      <div className={styles.metadataItem}>
        <Group gap="xs" align="center">
          <IconClock size={ICON_SIZE_TAB} />
          <Input.Label>{t('notes.detail.lastModified')}</Input.Label>
        </Group>
        <Text size="sm" className={styles.metadataValue}>
          {formatDateCompact(displayDate)}
        </Text>
      </div>

      {linkedTask && (
        <div className={clsx(styles.metadataItem, styles.metadataItemWide)}>
          <Group gap="xs" align="center">
            <IconLink size={ICON_SIZE_TAB} />
            <Input.Label>{t('notes.detail.linkedTask')}</Input.Label>
          </Group>
          {onTaskNavigate ? (
            <Text
              size="sm"
              className={styles.taskLink}
              onClick={handleTaskClick}
            >
              {linkedTask.title}
            </Text>
          ) : (
            <Text size="sm" className={styles.metadataValue}>
              {linkedTask.title}
            </Text>
          )}
        </div>
      )}
    </div>
  );
});
